import { AlertTriangle, ListFilter, RefreshCw, ScrollText } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { api } from "./api";
import "./operational-logs.css";

type LogLevel = "info" | "warn" | "error";
type LogFilter = "all" | "events" | "warnings" | "errors" | "reconnects";

interface OperationalLogRow {
  id: string;
  channel_id: string | null;
  level: LogLevel;
  event_name: string;
  message: string;
  correlation_id: string | null;
  metadata_json: string;
  created_at: string;
}

const previewLogs: OperationalLogRow[] = [
  {
    id: "preview-kick",
    channel_id: "preview",
    level: "info",
    event_name: "kick.event.processed",
    message: "chat.message.sent processed",
    correlation_id: "preview-ray-001",
    metadata_json: "{}",
    created_at: new Date(Date.now() - 22_000).toISOString(),
  },
  {
    id: "preview-timer",
    channel_id: "preview",
    level: "info",
    event_name: "timer.message.sent",
    message: "Community reminder posted after 14 qualifying chat messages.",
    correlation_id: "preview-timer-001",
    metadata_json: "{}",
    created_at: new Date(Date.now() - 3 * 60_000).toISOString(),
  },
  {
    id: "preview-warning",
    channel_id: "preview",
    level: "warn",
    event_name: "kick.event.unmatched",
    message: "No connected channel matched the incoming event.",
    correlation_id: "preview-ray-002",
    metadata_json: "{}",
    created_at: new Date(Date.now() - 8 * 60_000).toISOString(),
  },
  {
    id: "preview-reconnect",
    channel_id: "preview",
    level: "warn",
    event_name: "realtime.socket.reconnect",
    message: "OBS browser source reconnected after a dropped socket.",
    correlation_id: null,
    metadata_json: "{}",
    created_at: new Date(Date.now() - 15 * 60_000).toISOString(),
  },
  {
    id: "preview-error",
    channel_id: "preview",
    level: "error",
    event_name: "timer.message.failed",
    message: "Kick chat delivery failed during a timer send.",
    correlation_id: "preview-timer-002",
    metadata_json: "{}",
    created_at: new Date(Date.now() - 24 * 60_000).toISOString(),
  },
];

const filters: Array<{ value: LogFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "events", label: "Events" },
  { value: "warnings", label: "Warnings" },
  { value: "errors", label: "Errors" },
  { value: "reconnects", label: "Reconnects" },
];

function logTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--:--:--";
  return date.toLocaleTimeString([], { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function logDateTitle(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function matchesFilter(log: OperationalLogRow, filter: LogFilter): boolean {
  if (filter === "all") return true;
  if (filter === "warnings") return log.level === "warn";
  if (filter === "errors") return log.level === "error";
  const searchable = `${log.event_name} ${log.message}`.toLowerCase();
  if (filter === "reconnects") return searchable.includes("reconnect") || searchable.includes("socket");
  return log.event_name.startsWith("kick.") || log.event_name.includes(".event.") || log.event_name.includes("flow") || log.event_name.includes("giveaway") || log.event_name.includes("reward");
}

export function OperationalLogsPage({ preview }: { preview: boolean }) {
  const [logs, setLogs] = useState<OperationalLogRow[]>(preview ? previewLogs : []);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<LogFilter>("all");
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState("");

  async function refresh() {
    if (preview) {
      setLogs(previewLogs);
      return;
    }
    setLoading(true);
    setError("");
    try {
      setLogs(await api<OperationalLogRow[]>("/api/logs"));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load operational logs.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, [preview]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return logs.filter((log) => {
      if (!matchesFilter(log, filter)) return false;
      if (!needle) return true;
      return `${log.event_name} ${log.message} ${log.correlation_id ?? ""}`.toLowerCase().includes(needle);
    });
  }, [filter, logs, query]);

  return <div className="module-page">
    <div className="module-hero">
      <div><p className="eyebrow">Operations</p><h2>Logs</h2><p>A searchable record of Kick events, automation runs, moderation decisions, errors, and reconnects.</p></div>
      <button className="secondary-button" disabled={loading} onClick={() => void refresh()}><RefreshCw className={loading ? "spin" : ""} size={17} /> {loading ? "Refreshing…" : "Refresh"}</button>
    </div>

    {error && <div className="inline-error"><AlertTriangle size={16} /> {error}</div>}

    <div className="filter-bar">
      <label><ListFilter size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search event, message, or correlation ID" /></label>
      <div>{filters.map((item) => <button className={filter === item.value ? "active" : ""} key={item.value} onClick={() => setFilter(item.value)}>{item.label}</button>)}</div>
    </div>

    <div className="log-console">
      {filtered.map((log) => <div key={log.id}>
        <time title={logDateTitle(log.created_at)}>{logTime(log.created_at)}</time>
        <span className={`log-level ${log.level}`}>{log.level.toUpperCase()}</span>
        <strong>{log.event_name}</strong>
        <p>{log.message}{log.correlation_id ? ` · ${log.correlation_id}` : ""}</p>
      </div>)}
      {filtered.length === 0 && <div className="log-empty"><ScrollText size={18} /><p>{loading ? "Loading operational logs…" : logs.length === 0 ? "No operational logs have been recorded yet." : "No logs match this search or filter."}</p></div>}
    </div>
  </div>;
}
